import React, { useState } from 'react';
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../common/Loader";
import { API_BASE_URL } from "../config/constants";
import { X } from "lucide-react";
import { useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";


const NewReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const { token } = useParams(); // undefined if not present


  const [fieldError, setFieldError] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [email, setEmail] = useState(location.state?.email || "");

  const handleResetPassword = async () => {
    setLoading(true);
    setFieldError({ email: "", newPassword: "", confirmPassword: "" });
    setError("");

    // Require email only if token does NOT exist
    if (!token && !email) {
      setError("Email is required");
      setLoading(false);
      return;
    }

    if (!newPassword) {
      setFieldError({ newPassword: "New password is required" });
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setFieldError({ confirmPassword: "Passwords do not match" });
      setLoading(false);
      return;
    }

    try {
      const payload = token
        ? { token, password: newPassword }
        : { email, password: newPassword };

      await axios.post(`${API_BASE_URL}/reset-password`, payload);

      navigate("/", {
        replace: true,
        state: { message: "Password reset successful. Please login again." },
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || "Password reset failed");
      } else {
        setError("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#073631_0%,#07201D_100%)] flex flex-col justify-center items-center px-4 py-8 overflow-hidden">
      {/* Error Alert */}
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 max-w-sm w-full px-4 z-50">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center justify-between text-sm">
            <div>
              <strong className="font-bold">Error! </strong>
              {error}
            </div>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reset  Card */}
      <div className="w-full max-w-[38rem] min-h-[44.875rem] bg-[#051E1C] backdrop-blur-sm rounded-2xl shadow-2xl px-[5.0625rem] pt-[5.5rem] pb-[6.5625rem]">
        {/* Logo */}
        <div className="flex mb-[4.125rem]">
          <img src="assets/kirloskar-logo.svg" alt="Logo" className="w-[16.6159rem] h-[3.125rem]" />
        </div>

        {/* Title */}
        <h2 className="text-[2.25rem] font-poppins font-semibold text-white mb-[2.125rem]">
          Reset Password
        </h2>



        {/* Form */}
        <div className="">
          {/* New Password */}
          <div className="mb-[1.25rem]">
            <label className="block text-[#D9D9D9] text-[1.125rem] font-normal mb-[0.375rem]">
              Enter Password*
            </label>

            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setFieldError((prev) => ({ ...prev, newPassword: "" }));
                }}
                className={`w-[27.3125rem] py-[0.625rem] px-[0.875rem] h-[3.4375rem] bg-[#33504D75] text-white rounded-lg text-[1.125rem]
        ${fieldError.newPassword
                    ? "border border-red-500 focus:ring-red-500/40"
                    : "focus:border-gray-300 focus:ring-teal-500/50"
                  }`}
              />
              <div
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-[2.75rem] flex items-center cursor-pointer"
              >
                <img
                  src={showNewPassword ? "/assets/eye-open.svg" : "/assets/eye-off.svg"}
                  alt="toggle password visibility"
                  className="h-[1.5rem] w-[1.5rem] invert"
                />
              </div>
            </div>

            {/* Reserve space for error so UI doesn't shift */}
            <p className={`mt-1 text-sm ${fieldError.newPassword ? "text-red-500" : "text-transparent"}`}>
              {fieldError.newPassword || "Placeholder"}
            </p>
          </div>


          {/* Confirm Password */}
          <div className="mb-[4.8125rem]">
            <label className="block text-[#D9D9D9] text-[1.125rem] font-normal mb-[0.375rem]">
              Confirm Password*
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldError((prev) => ({ ...prev, confirmPassword: "" }));
                }}
                className={`w-[27.3125rem] py-[0.625rem] px-[0.875rem] h-[3.4375rem] bg-[#33504D75] text-white rounded-lg text-[1.125rem]
                  ${fieldError.confirmPassword
                    ? "border border-red-500 focus:ring-red-500/40"
                    : "focus:border-gray-300 focus:ring-teal-500/50"
                  }`}
              />
              <div
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-[2.75rem] flex items-center cursor-pointer"
              >
                <img
                  src={showConfirmPassword ? "/assets/eye-open.svg" : "/assets/eye-off.svg"}
                  alt="toggle password visibility"
                  className="h-[1.5rem] w-[1.5rem] invert"
                />
              </div>
            </div>
            {fieldError.confirmPassword && (
              <p className="mt-1 text-sm text-red-500">{fieldError.confirmPassword}</p>
            )}
          </div>
          {/* Reset Button */}
          <div className="">
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full text-[1.375rem] font-semibold max-w-[27.3125rem] min-h-[3.4375rem] bg-[#147A6F] text-[#FFFFFF] py-[0.625rem] px-[1.125rem] rounded-[6.25rem]"
            >
              Reset Password
            </button>
          </div>


        </div>
      </div>
    </div>
  );
};

export default NewReset;